import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { LifeBuoy, Plus, Trash2, Loader2, Lock, BellRing } from "lucide-react";

const EMPTY = { name: "", phone: "", email: "" };

export function EmergencyContacts() {
  const [contacts, setContacts] = useState([]);
  const [pings, setPings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get("/profile/emergency-contacts"),
        api.get("/profile/safety-pings"),
      ]);
      setContacts((c.data.contacts || []).map((x) => ({ ...x, _uid: crypto.randomUUID() })));
      setPings(p.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upd = (i, k, v) => setContacts(contacts.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const add = () => { if (contacts.length < 5) setContacts([...contacts, { ...EMPTY, _uid: crypto.randomUUID() }]); };
  const remove = (i) => setContacts(contacts.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = contacts.filter((c) => c.name.trim()).map(({ _uid, ...rest }) => rest);
      const { data } = await api.put("/profile/emergency-contacts", { contacts: cleaned });
      setContacts((data.contacts || []).map((x) => ({ ...x, _uid: crypto.randomUUID() })));
      toast.success("Emergency contacts saved");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="emergency-contacts" className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6 sm:p-8 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <LifeBuoy size={18} className="text-primary" />
        <h2 className="font-heading font-extrabold text-lg text-[#1A1D20]">Safety contacts</h2>
      </div>
      <p className="text-sm text-[#6B7280] mb-1">
        Add up to <strong>5 people</strong> (family or guardians). They're pinged when you <strong>start</strong> an errand and again when you <strong>complete</strong> it — so someone always has your back.
      </p>
      <p className="text-[11px] text-[#2C6FA0] bg-[#EEF6FF] border border-[#D6E7FA] rounded-xl px-3 py-2 flex items-center gap-1.5 mb-5">
        <Lock size={12} /> These contacts are private to you. Customers never see them.
      </p>

      {loading ? (
        <div className="flex justify-center py-6 text-[#6B7280]"><Loader2 className="animate-spin" /></div>
      ) : (
        <>
          <div className="space-y-3 mb-4">
            {contacts.length === 0 && <p className="text-sm text-[#9CA3AF] text-center py-4">No safety contacts yet.</p>}
            {contacts.map((c, i) => (
              <div key={c._uid} data-testid={`contact-row-${i}`} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                <Input data-testid={`contact-name-${i}`} value={c.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Name"
                  className="rounded-2xl h-11 border-gray-200" />
                <Input data-testid={`contact-phone-${i}`} value={c.phone} onChange={(e) => upd(i, "phone", e.target.value)} placeholder="Phone"
                  className="rounded-2xl h-11 border-gray-200" />
                <Input data-testid={`contact-email-${i}`} value={c.email} onChange={(e) => upd(i, "email", e.target.value)} placeholder="Email"
                  className="rounded-2xl h-11 border-gray-200" />
                <Button data-testid={`contact-remove-${i}`} onClick={() => remove(i)} variant="ghost" size="icon"
                  className="rounded-full text-[#B23B3B] hover:bg-[#FBE9E9] justify-self-start sm:justify-self-auto">
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button data-testid="add-contact-btn" onClick={add} disabled={contacts.length >= 5} variant="outline"
              className="rounded-full font-semibold border-2">
              <Plus size={16} className="mr-1.5" /> Add contact {contacts.length > 0 && `(${contacts.length}/5)`}
            </Button>
            <Button data-testid="save-contacts-btn" onClick={save} disabled={saving}
              className="rounded-full font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
              {saving ? <Loader2 className="animate-spin" size={16} /> : "Save contacts"}
            </Button>
          </div>

          {pings.length > 0 && (
            <div className="mt-6 pt-5 border-t border-gray-100">
              <p className="text-xs font-semibold uppercase text-[#9CA3AF] mb-2 flex items-center gap-1"><BellRing size={12} /> Recent safety alerts sent</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto no-scrollbar">
                {pings.slice(0, 12).map((p) => (
                  <div key={p.id} data-testid={`ping-${p.id}`} className="flex items-center justify-between text-xs">
                    <span className="text-[#4A5361]">
                      <span className={`font-bold ${p.type === "completed" ? "text-[#2F8F6E]" : "text-[#C4531F]"}`}>{p.type === "completed" ? "Completed" : "Started"}</span>
                      {" "}· {p.contact_name} · "{p.errand_title}"
                    </span>
                    <span className="text-[#9CA3AF]">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
